import { Button } from "@frontend/components/ui/button"
import { Slider } from "@frontend/components/ui/slider"
import { Textarea } from "@frontend/components/ui/textarea"
import { ToggleGroup, ToggleGroupItem } from "@frontend/components/ui/toggle-group"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@frontend/components/ui/tooltip"
import { useToggleSet } from "@frontend/hooks/use-toggle-set"
import type { ModuleDependencyLockState } from "@frontend/pages/case-workspace/lib/module-dependency-lock"
import { EditRegionsCard } from "@frontend/pages/case-workspace/pathology/components/edit-regions-card"
import {
  LabelRasterModulePanel,
  type LabelRasterModulePanelProps
} from "@frontend/pages/case-workspace/pathology/components/label-raster-module-panel"
import { moduleTypeIcon } from "@frontend/pages/case-workspace/pathology/components/module-schema"
import type { EditRegionsState } from "@frontend/pages/case-workspace/pathology/hooks/use-edit-regions"
import { getEffectiveOpacity } from "@frontend/pages/case-workspace/pathology/lib/pathology-vector-opacity"
import type {
  ImageLabelSchema,
  ParsedModuleSchema,
  ProjectModuleItem,
  ScoreSchema,
  TextSchema,
  VectorGroup,
  VectorLabel,
  VectorTool
} from "@frontend/pages/case-workspace/pathology/types"
import type {
  ImageLabelAnnotationState,
  ModuleCompletionState,
  ScoreAnnotationState,
  TextAnnotationState,
  VectorAnnotationState
} from "@shared/lib/history-types"
import {
  AlertCircle,
  Brush,
  Check,
  ChevronDown,
  ChevronRight,
  Eraser,
  Eye,
  EyeOff,
  Lock,
  PenTool,
  Sparkles,
  Trash2
} from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"

type AnnotationGroupBucket = {
  group: VectorGroup | null
  key: string
  name: string
  vectors: VectorAnnotationState[]
}

const EMPTY_ANNOTATION_GROUPS: AnnotationGroupBucket[] = []

const VECTOR_TOOL_ITEMS = [
  { value: "pen", icon: PenTool, tip: "Pen (P) — polygon tool" },
  { value: "brush", icon: Brush, tip: "Brush (B) — inflated polygon" },
  { value: "erase", icon: Eraser, tip: "Eraser (E)" },
  { value: "sam", icon: Sparkles, tip: "SAM (S) — AI segmentation" }
] as const

function CollapsibleSectionHeader({
  collapsed,
  onToggle,
  label,
  labelClassName,
  count
}: {
  collapsed: boolean
  onToggle: () => void
  label: string
  labelClassName?: string
  count?: number
}) {
  return (
    <button className="flex items-center gap-1 w-full text-left" onClick={onToggle}>
      {collapsed ? (
        <ChevronRight className="size-3 text-muted-foreground" />
      ) : (
        <ChevronDown className="size-3 text-muted-foreground" />
      )}
      <span className={labelClassName ?? "text-[10px] font-medium text-muted-foreground uppercase tracking-wide"}>
        {label}
      </span>
      {count != null && count > 0 && <span className="text-[10px] text-muted-foreground tabular-nums">({count})</span>}
    </button>
  )
}

function VectorAnnotationCard({
  vector,
  activeVectorLabelByKey,
  vectorOpacityDraftById,
  isActiveModuleEditable,
  isSelected,
  onSelect,
  onVectorVisibilityToggle,
  onVectorDelete,
  onVectorOpacityChange,
  onVectorOpacityCommit
}: {
  vector: VectorAnnotationState
  activeVectorLabelByKey: Map<string, VectorLabel>
  vectorOpacityDraftById: Record<string, number>
  isActiveModuleEditable: boolean
  isSelected: boolean
  onSelect: () => void
  onVectorVisibilityToggle: (vector: VectorAnnotationState, opacityPercent: number) => void
  onVectorDelete: (id: string) => void
  onVectorOpacityChange: (vector: VectorAnnotationState, percent: number) => void
  onVectorOpacityCommit: (vector: VectorAnnotationState, percent: number) => void
}) {
  const cardRef = useRef<HTMLDivElement>(null)
  const currentOpacity = getEffectiveOpacity(vector.annotation.style)
  const committedOpacityPercent = Math.round(currentOpacity * 100)
  const opacityPercent = vectorOpacityDraftById[vector.id] ?? committedOpacityPercent
  const isVisible = opacityPercent > 0
  const label = (vector.labelName ? activeVectorLabelByKey.get(vector.labelName) : null) ?? null
  const labelText = label?.label ?? vector.labelName ?? vector.annotation.label ?? "Unlabeled"
  const labelColor = label?.color ?? vector.annotation.style.strokeColor

  useEffect(() => {
    if (isSelected && cardRef.current) {
      cardRef.current.scrollIntoView({ block: "nearest" })
    }
  }, [isSelected])

  return (
    <div
      ref={cardRef}
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          onSelect()
        }
      }}
      className={`rounded-md border px-2.5 py-2 cursor-pointer transition-colors ${
        isSelected ? "border-primary bg-primary/10 ring-1 ring-primary/40" : "border-border bg-surface-raised"
      } ${isVisible ? "" : "opacity-80"}`}
    >
      <div className="flex items-center gap-1.5">
        <span className="size-3 rounded-sm shrink-0 border border-white/20" style={{ backgroundColor: labelColor }} />
        <span className="flex-1 min-w-0 text-[11px] font-medium truncate">{labelText}</span>
        <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">{opacityPercent}%</span>
        <Button
          size="icon"
          variant="ghost"
          className="size-6"
          disabled={!isActiveModuleEditable}
          title={isVisible ? "Hide vector" : "Show vector"}
          onClick={() => onVectorVisibilityToggle(vector, opacityPercent)}
        >
          {isVisible ? <Eye className="size-3.5" /> : <EyeOff className="size-3.5" />}
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="size-6 text-destructive"
          disabled={!isActiveModuleEditable}
          title="Delete vector"
          onClick={() => {
            if (!isActiveModuleEditable) return
            if (!window.confirm("Delete this vector annotation?")) return
            onVectorDelete(vector.id)
          }}
        >
          <Trash2 className="size-3.5" />
        </Button>
      </div>
      <div className="mt-2">
        <Slider
          value={[opacityPercent]}
          min={0}
          max={100}
          step={1}
          disabled={!isActiveModuleEditable}
          onValueChange={(value) => {
            const next = Array.isArray(value) ? (value[0] ?? 0) : value
            onVectorOpacityChange(vector, next)
          }}
          onValueCommitted={(value) => {
            const next = Array.isArray(value) ? (value[0] ?? 0) : value
            onVectorOpacityCommit(vector, next)
          }}
        />
      </div>
      <div className="mt-1 text-[9px] text-muted-foreground font-mono truncate">{vector.id}</div>
    </div>
  )
}

function ScoreModulePanel({
  schema,
  moduleId,
  seriesId,
  scores,
  onUpsertScore,
  readOnly
}: {
  schema: ScoreSchema
  moduleId: string
  seriesId: string | null
  scores: ScoreAnnotationState[]
  onUpsertScore: (score: ScoreAnnotationState) => void
  readOnly: boolean
}) {
  const [generatedId] = useState(() => crypto.randomUUID())
  const existing = scores.find((s) => s.projectModuleId === moduleId && s.seriesId === seriesId && s.labelName === null)
  const currentId = existing?.id ?? generatedId

  function setValue(key: string, value: string | number | boolean | null) {
    if (readOnly) return
    onUpsertScore({
      id: currentId,
      seriesId,
      projectModuleId: moduleId,
      labelName: null,
      labelValue: null,
      values: { ...(existing?.values ?? {}), [key]: value }
    })
  }

  return (
    <div className="space-y-3">
      {schema.fields.map((field) => {
        const currentVal = existing?.values[field.key] ?? null
        return (
          <div key={field.key}>
            <div className="text-[11px] font-medium mb-1.5">{field.label}</div>
            {field.type === "categorical" && field.options ? (
              <div className="flex flex-wrap gap-1">
                {field.options.map((opt) => (
                  <button
                    key={String(opt.value)}
                    disabled={readOnly}
                    onClick={() => setValue(field.key, opt.value)}
                    className={`px-2.5 py-1 rounded-md border text-[11px] transition-colors ${
                      currentVal === opt.value
                        ? "border-primary/60 bg-primary/10"
                        : "border-border bg-surface-raised hover:bg-accent/40"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            ) : field.type === "likert" ? (
              <div className="flex gap-1">
                {(field.options ? field.options.map((o) => o.value) : [1, 2, 3, 4, 5]).map((v) => (
                  <button
                    key={String(v)}
                    disabled={readOnly}
                    onClick={() => setValue(field.key, v)}
                    className={`flex-1 py-1 rounded-md border text-[11px] font-medium transition-colors ${
                      currentVal === v
                        ? "border-primary/60 bg-primary/10"
                        : "border-border bg-surface-raised hover:bg-accent/40"
                    }`}
                  >
                    {String(v)}
                  </button>
                ))}
              </div>
            ) : (
              <input
                type="number"
                value={typeof currentVal === "number" ? String(currentVal) : ""}
                disabled={readOnly}
                onChange={(e) => setValue(field.key, e.target.value === "" ? null : Number(e.target.value))}
                className="w-full rounded-md border border-border bg-surface-raised px-2.5 py-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-primary/50"
                placeholder="Enter value"
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

function ImageLabelModulePanel({
  schema,
  moduleId,
  seriesId,
  imageLabels,
  onUpsertImageLabel,
  readOnly
}: {
  schema: ImageLabelSchema
  moduleId: string
  seriesId: string | null
  imageLabels: ImageLabelAnnotationState[]
  onUpsertImageLabel: (label: ImageLabelAnnotationState) => void
  readOnly: boolean
}) {
  const [generatedId] = useState(() => crypto.randomUUID())
  const existing = imageLabels.find((l) => l.projectModuleId === moduleId && l.seriesId === seriesId)
  const currentId = existing?.id ?? generatedId

  function toggleOption(value: string) {
    if (readOnly) return
    const current = existing?.labels ?? {}
    const nextLabels = schema.multi_select
      ? { ...current, [value]: !current[value] }
      : Object.fromEntries(schema.options.map((o) => [o.value, o.value === value ? !current[value] : false]))

    onUpsertImageLabel({
      id: currentId,
      seriesId,
      projectModuleId: moduleId,
      labels: nextLabels
    })
  }

  return (
    <div className="space-y-1">
      {schema.options.map((opt) => {
        const selected = existing?.labels[opt.value] ?? false
        return (
          <button
            key={opt.value}
            disabled={readOnly}
            onClick={() => toggleOption(opt.value)}
            className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md border text-left transition-colors ${
              selected ? "border-primary/60 bg-primary/10" : "border-border bg-surface-raised hover:bg-accent/40"
            }`}
          >
            <span
              className={`size-3.5 rounded-sm shrink-0 border flex items-center justify-center ${
                selected ? "bg-primary border-primary" : "border-border"
              }`}
            >
              {selected && <Check className="size-2.5 text-primary-foreground" />}
            </span>
            <span className="text-[11px] font-medium">{opt.label}</span>
          </button>
        )
      })}
    </div>
  )
}

function TextModulePanel({
  schema,
  moduleId,
  activeSeriesId,
  texts,
  onUpsertText,
  readOnly
}: {
  schema: TextSchema
  moduleId: string
  activeSeriesId: string | null
  texts: TextAnnotationState[]
  onUpsertText: (text: TextAnnotationState) => void
  readOnly: boolean
}) {
  const targetSeriesId = schema.scope === "series" ? activeSeriesId : null
  const [generatedId] = useState(() => crypto.randomUUID())
  const existing = texts.find((t) => t.projectModuleId === moduleId && t.seriesId === targetSeriesId)
  const currentId = existing?.id ?? generatedId

  return (
    <Textarea
      value={existing?.valueText ?? ""}
      disabled={readOnly}
      placeholder={schema.placeholder ?? "Add notes"}
      maxLength={schema.max_length}
      rows={8}
      onChange={(e) =>
        onUpsertText({
          id: currentId,
          seriesId: targetSeriesId,
          projectModuleId: moduleId,
          valueText: e.target.value
        })
      }
    />
  )
}

interface PathologyModuleSidebarProps {
  projectModules: ProjectModuleItem[]
  moduleLockById: Map<string, ModuleDependencyLockState>
  activeModule: ProjectModuleItem | null
  activeModuleSchema: ParsedModuleSchema
  moduleCompletions: ModuleCompletionState[]
  onSelectModule: (moduleId: string) => void
  activeVectorLabelKey: string | null
  activeVectorTool: VectorTool | null
  vectorEraserRadiusPx: number
  vectorBrushRadiusPx: number
  maxToolRadius: number
  onToggleVectorLabel: (labelKey: string) => void
  onVectorToolChange: (tool: VectorTool | null) => void
  onVectorEraserRadiusChange: (radiusPx: number) => void
  onVectorBrushRadiusChange: (radiusPx: number) => void
  activeVectorGroups: VectorGroup[]
  hiddenGroupKeys: Set<string>
  onToggleGroupVisibility: (groupKey: string) => void
  activeModuleVectors: VectorAnnotationState[]
  activeVectorLabelByKey: Map<string, VectorLabel>
  vectorOpacityDraftById: Record<string, number>
  onVectorVisibilityToggle: (vector: VectorAnnotationState, currentOpacityPercent?: number) => void
  onVectorDelete: (vectorId: string) => void
  onVectorOpacityChange: (vector: VectorAnnotationState, opacityPercent: number) => void
  onVectorOpacityCommit: (vector: VectorAnnotationState, opacityPercent: number) => void
  selectedAnnotationId: string | null
  onSelectAnnotation: (id: string | null) => void
  activeSeriesId: string | null
  imageLabels: ImageLabelAnnotationState[]
  onUpsertImageLabel: (label: ImageLabelAnnotationState) => void
  scores: ScoreAnnotationState[]
  onUpsertScore: (score: ScoreAnnotationState) => void
  texts: TextAnnotationState[]
  onUpsertText: (text: TextAnnotationState) => void
  isActiveModuleComplete: boolean
  isActiveModuleEditable: boolean
  activeModuleLockReason: string | null
  canComplete: boolean
  onMarkComplete: () => void
  /** Optional list of human-readable reasons the module cannot be marked complete yet. */
  completionErrors?: string[] | null
  /** Props for the in-place label mask editing panel (label_raster modules). */
  labelRaster?: LabelRasterModulePanelProps | null
  /** Edit regions confining the active module (vector modules show the Regions card). */
  editRegions?: EditRegionsState | null
  /** The edit-bbox request failed while the active module needs them: drawing is disabled. */
  editRegionsError?: boolean
}

export function PathologyModuleSidebar({
  projectModules,
  moduleLockById,
  activeModule,
  activeModuleSchema,
  moduleCompletions,
  onSelectModule,
  activeVectorLabelKey,
  activeVectorTool,
  vectorEraserRadiusPx,
  vectorBrushRadiusPx,
  maxToolRadius,
  onToggleVectorLabel,
  onVectorToolChange,
  onVectorEraserRadiusChange,
  onVectorBrushRadiusChange,
  activeVectorGroups,
  hiddenGroupKeys,
  onToggleGroupVisibility,
  activeModuleVectors,
  activeVectorLabelByKey,
  vectorOpacityDraftById,
  onVectorVisibilityToggle,
  onVectorDelete,
  onVectorOpacityChange,
  onVectorOpacityCommit,
  selectedAnnotationId,
  onSelectAnnotation,
  activeSeriesId,
  imageLabels,
  onUpsertImageLabel,
  scores,
  onUpsertScore,
  texts,
  onUpsertText,
  isActiveModuleComplete,
  isActiveModuleEditable,
  activeModuleLockReason,
  canComplete,
  onMarkComplete,
  completionErrors,
  labelRaster,
  editRegions = null,
  editRegionsError = false
}: PathologyModuleSidebarProps) {
  const [expandedGroups, toggleGroupExpanded, clearExpandedGroups] = useToggleSet<string>()
  const [expandedAnnotationGroups, toggleAnnotationGroupExpanded, clearExpandedAnnotationGroups] =
    useToggleSet<string>()
  const [annotationsCollapsed, setAnnotationsCollapsed] = useState(true)

  const prevModuleIdRef = useRef(activeModule?.id)
  useEffect(() => {
    if (activeModule?.id !== prevModuleIdRef.current) {
      prevModuleIdRef.current = activeModule?.id
      clearExpandedGroups()
      clearExpandedAnnotationGroups()
      setAnnotationsCollapsed(true)
    }
  }, [activeModule?.id, clearExpandedGroups, clearExpandedAnnotationGroups])

  const groupedAnnotationVectors = useMemo((): AnnotationGroupBucket[] => {
    if (!activeModuleVectors.length) return EMPTY_ANNOTATION_GROUPS
    if (!activeVectorGroups.length) {
      return [{ group: null, key: "__ungrouped__", name: "Ungrouped", vectors: activeModuleVectors }]
    }
    const labelToGroup = new Map<string, string>()
    const buckets = new Map<string, VectorAnnotationState[]>()
    for (const group of activeVectorGroups) {
      buckets.set(group.key, [])
      for (const lbl of group.labels) {
        labelToGroup.set(lbl.key, group.key)
      }
    }
    const ungrouped: VectorAnnotationState[] = []
    for (const vector of activeModuleVectors) {
      const groupKey = vector.labelName ? labelToGroup.get(vector.labelName) : undefined
      const bucket = groupKey ? buckets.get(groupKey) : undefined
      if (bucket) {
        bucket.push(vector)
      } else {
        ungrouped.push(vector)
      }
    }
    const result: AnnotationGroupBucket[] = []
    for (const group of activeVectorGroups) {
      const vecs = buckets.get(group.key)!
      if (vecs.length > 0) {
        result.push({ group, key: group.key, name: group.name, vectors: vecs })
      }
    }
    if (ungrouped.length > 0) {
      result.push({ group: null, key: "__ungrouped__", name: "Ungrouped", vectors: ungrouped })
    }
    return result
  }, [activeModuleVectors, activeVectorGroups])

  // Auto-expand annotations section and containing group when an annotation is selected on canvas
  const groupedAnnotationVectorsRef = useRef(groupedAnnotationVectors)
  useEffect(() => {
    groupedAnnotationVectorsRef.current = groupedAnnotationVectors
  }, [groupedAnnotationVectors])
  useEffect(() => {
    if (!selectedAnnotationId) return
    setAnnotationsCollapsed(false)
    const bucket = groupedAnnotationVectorsRef.current.find((b) => b.vectors.some((v) => v.id === selectedAnnotationId))
    if (bucket && !expandedAnnotationGroups.has(bucket.key)) {
      toggleAnnotationGroupExpanded(bucket.key)
    }
  }, [selectedAnnotationId, expandedAnnotationGroups, toggleAnnotationGroupExpanded])

  return (
    <aside className="bg-card flex flex-1 min-h-0 flex-col">
      <div className="px-2.5 py-2 text-[10px] font-medium text-muted-foreground uppercase tracking-wide border-b border-border">
        Modules
      </div>
      <div className="overflow-auto border-b border-border shrink-0">
        {(projectModules ?? []).map((module) => {
          const done = moduleCompletions.find((m) => m.projectModuleId === module.id)?.completed ?? false
          const lock = moduleLockById.get(module.id)
          const isLocked = lock?.isEditable === false
          const selected = activeModule?.id === module.id
          return (
            <button
              key={module.id}
              onClick={() => onSelectModule(module.id)}
              title={isLocked ? (lock.lockReason ?? "Module is locked") : undefined}
              className={`w-full text-left px-3 py-2 flex items-center gap-2 transition-colors border-b border-border/50 last:border-b-0 ${
                selected ? "bg-primary/10" : "hover:bg-accent/40"
              }`}
            >
              {moduleTypeIcon(module.template_type)}
              <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
                <span className="truncate text-[11px] font-medium">{module.template_name}</span>
              </div>
              {isLocked && <Lock className="size-3 text-muted-foreground shrink-0" />}
              {done && <Check className="size-3 text-green-500 shrink-0" />}
            </button>
          )
        })}
      </div>

      <div className="px-3 py-2 border-b border-border shrink-0">
        <div className="text-[11px] font-semibold truncate">{activeModule?.template_name ?? "No module selected"}</div>
        <div className="text-[10px] text-muted-foreground uppercase">{activeModule?.template_type ?? "—"}</div>
      </div>

      <div className="flex flex-1 min-h-0 flex-col overflow-auto p-3">
        {!activeModule ? (
          <div className="text-[11px] text-muted-foreground">No module selected</div>
        ) : activeModule.template_type === "vector" && activeModuleSchema?.type === "vector" ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Tool</div>
              <TooltipProvider>
                <ToggleGroup variant="outline" size="sm" className="w-full">
                  {VECTOR_TOOL_ITEMS.map(({ value, icon: Icon, tip }) => (
                    <Tooltip key={value}>
                      <TooltipTrigger render={<span className="flex-1" />}>
                        <ToggleGroupItem
                          value={value}
                          pressed={activeVectorTool === value}
                          onPressedChange={() => onVectorToolChange(activeVectorTool === value ? null : value)}
                          disabled={!isActiveModuleEditable}
                          className={`h-7 w-full p-0 ${
                            activeVectorTool === value
                              ? "border-primary/70 bg-primary/20 text-primary aria-pressed:bg-primary/20"
                              : ""
                          }`}
                        >
                          <Icon className="size-3.5" />
                        </ToggleGroupItem>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">{tip}</TooltipContent>
                    </Tooltip>
                  ))}
                </ToggleGroup>
              </TooltipProvider>
              {activeVectorTool === "brush" && (
                <div className="space-y-1.5 rounded-md border border-border bg-surface-raised px-2.5 py-2">
                  <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                    <span>Brush radius</span>
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        aria-label="Brush radius in pixels"
                        min={2}
                        max={maxToolRadius}
                        step={1}
                        disabled={!isActiveModuleEditable}
                        value={Math.round(vectorBrushRadiusPx)}
                        onChange={(e) => {
                          const v = Math.max(2, Math.min(maxToolRadius, Math.round(Number(e.target.value) || 2)))
                          onVectorBrushRadiusChange(v)
                        }}
                        className="w-12 h-5 text-[10px] font-mono tabular-nums text-right bg-transparent border border-border rounded px-1 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      />
                      <span className="font-mono tabular-nums">px</span>
                    </div>
                  </div>
                  <Slider
                    value={[Math.min(maxToolRadius, Math.max(2, vectorBrushRadiusPx))]}
                    min={2}
                    max={maxToolRadius}
                    step={1}
                    disabled={!isActiveModuleEditable}
                    onValueChange={(value) => {
                      const next = Array.isArray(value) ? (value[0] ?? vectorBrushRadiusPx) : value
                      onVectorBrushRadiusChange(Math.max(2, Math.round(next)))
                    }}
                    onValueCommitted={(value) => {
                      const next = Array.isArray(value) ? (value[0] ?? vectorBrushRadiusPx) : value
                      onVectorBrushRadiusChange(Math.max(2, Math.round(next)))
                    }}
                  />
                </div>
              )}
              {activeVectorTool === "erase" && (
                <div className="space-y-1.5 rounded-md border border-border bg-surface-raised px-2.5 py-2">
                  <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                    <span>Eraser radius</span>
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        aria-label="Eraser radius in pixels"
                        min={1}
                        max={maxToolRadius}
                        step={1}
                        disabled={!isActiveModuleEditable}
                        value={Math.round(vectorEraserRadiusPx)}
                        onChange={(e) => {
                          const v = Math.max(1, Math.min(maxToolRadius, Math.round(Number(e.target.value) || 1)))
                          onVectorEraserRadiusChange(v)
                        }}
                        className="w-12 h-5 text-[10px] font-mono tabular-nums text-right bg-transparent border border-border rounded px-1 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      />
                      <span className="font-mono tabular-nums">px</span>
                    </div>
                  </div>
                  <Slider
                    value={[Math.min(maxToolRadius, Math.max(1, vectorEraserRadiusPx))]}
                    min={1}
                    max={maxToolRadius}
                    step={1}
                    disabled={!isActiveModuleEditable}
                    onValueChange={(value) => {
                      const next = Array.isArray(value) ? (value[0] ?? vectorEraserRadiusPx) : value
                      onVectorEraserRadiusChange(Math.max(1, Math.round(next)))
                    }}
                    onValueCommitted={(value) => {
                      const next = Array.isArray(value) ? (value[0] ?? vectorEraserRadiusPx) : value
                      onVectorEraserRadiusChange(Math.max(1, Math.round(next)))
                    }}
                  />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Labels</div>
              {activeVectorGroups.map((group) => {
                const isGroupHidden = hiddenGroupKeys.has(group.key)
                const isGroupCollapsed = !expandedGroups.has(group.key)
                const iconBtnClass =
                  "size-5 flex items-center justify-center rounded hover:bg-accent/40 transition-colors"
                return (
                  <div key={group.key} className="space-y-1">
                    <div className="flex items-center gap-1">
                      <button
                        className={iconBtnClass}
                        title={isGroupCollapsed ? `Expand ${group.name}` : `Collapse ${group.name}`}
                        onClick={() => toggleGroupExpanded(group.key)}
                      >
                        {isGroupCollapsed ? (
                          <ChevronRight className="size-3 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="size-3 text-muted-foreground" />
                        )}
                      </button>
                      <button
                        className={iconBtnClass}
                        title={isGroupHidden ? `Show ${group.name}` : `Hide ${group.name}`}
                        onClick={() => onToggleGroupVisibility(group.key)}
                      >
                        {isGroupHidden ? (
                          <EyeOff className="size-3 text-muted-foreground" />
                        ) : (
                          <Eye className="size-3 text-muted-foreground" />
                        )}
                      </button>
                      <span className="text-[10px] font-semibold truncate">{group.name}</span>
                    </div>
                    {!isGroupCollapsed && (
                      <div className={`pl-1.5 space-y-1 ${isGroupHidden ? "opacity-40" : ""}`}>
                        {group.labels.map((lbl) => (
                          <button
                            key={lbl.key}
                            disabled={!isActiveModuleEditable || isGroupHidden}
                            onClick={() => onToggleVectorLabel(lbl.key)}
                            className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md border text-left transition-colors ${
                              activeVectorLabelKey === lbl.key
                                ? "border-primary/60 bg-primary/10"
                                : "border-border bg-surface-raised hover:bg-accent/40"
                            }`}
                          >
                            <span
                              className="size-3 rounded-sm shrink-0 border border-white/20"
                              style={{ backgroundColor: lbl.color }}
                            />
                            <span className="text-[11px] font-medium truncate">{lbl.label}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
              <p className="text-[10px] text-muted-foreground">
                {activeVectorTool === "erase"
                  ? "Erase mode edits any overlapping vector polygon"
                  : activeVectorTool === "sam"
                    ? "Click or drag a box to segment with SAM"
                    : activeVectorLabelKey
                      ? `${activeVectorTool === "pen" ? "Pen" : "Brush"} mode enabled for selected label`
                      : "Select a label to enable drawing"}
              </p>
            </div>

            {editRegionsError && (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 px-2.5 py-2 text-[10px] text-destructive">
                Could not load the drawing regions for this slide, so drawing is disabled. Reload the page to try again.
              </div>
            )}
            {editRegions && (
              <EditRegionsCard
                regions={editRegions.regions}
                activity="Drawing"
                hidden={editRegions.hidden}
                onHiddenChange={editRegions.setHidden}
                onJumpToRegion={editRegions.jumpToRegion}
                onNextRegion={editRegions.nextRegion}
              />
            )}

            <div className="space-y-2">
              <CollapsibleSectionHeader
                collapsed={annotationsCollapsed}
                onToggle={() => setAnnotationsCollapsed((c) => !c)}
                label="Annotations"
                count={activeModuleVectors.length}
              />
              {!annotationsCollapsed &&
                (activeModuleVectors.length === 0 ? (
                  <div className="rounded-md border border-dashed border-border px-2.5 py-2 text-[10px] text-muted-foreground">
                    No vector annotations yet
                  </div>
                ) : (
                  <div className="space-y-2">
                    {groupedAnnotationVectors.map((bucket) => (
                      <div key={bucket.key} className="space-y-1">
                        <CollapsibleSectionHeader
                          collapsed={!expandedAnnotationGroups.has(bucket.key)}
                          onToggle={() => toggleAnnotationGroupExpanded(bucket.key)}
                          label={bucket.name}
                          labelClassName="text-[10px] font-semibold truncate"
                          count={bucket.vectors.length}
                        />
                        {expandedAnnotationGroups.has(bucket.key) && (
                          <div className="pl-1.5 space-y-2">
                            {bucket.vectors.map((vector) => (
                              <VectorAnnotationCard
                                key={vector.id}
                                vector={vector}
                                activeVectorLabelByKey={activeVectorLabelByKey}
                                vectorOpacityDraftById={vectorOpacityDraftById}
                                isActiveModuleEditable={isActiveModuleEditable}
                                isSelected={vector.id === selectedAnnotationId}
                                onSelect={() =>
                                  onSelectAnnotation(vector.id === selectedAnnotationId ? null : vector.id)
                                }
                                onVectorVisibilityToggle={onVectorVisibilityToggle}
                                onVectorDelete={onVectorDelete}
                                onVectorOpacityChange={onVectorOpacityChange}
                                onVectorOpacityCommit={onVectorOpacityCommit}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
            </div>
          </div>
        ) : activeModule.template_type === "image_label" && activeModuleSchema?.type === "image_label" ? (
          <ImageLabelModulePanel
            schema={activeModuleSchema}
            moduleId={activeModule.id}
            seriesId={activeSeriesId}
            imageLabels={imageLabels}
            onUpsertImageLabel={onUpsertImageLabel}
            readOnly={!isActiveModuleEditable}
          />
        ) : activeModule.template_type === "score" && activeModuleSchema?.type === "score" ? (
          <ScoreModulePanel
            schema={activeModuleSchema}
            moduleId={activeModule.id}
            seriesId={activeSeriesId}
            scores={scores}
            onUpsertScore={onUpsertScore}
            readOnly={!isActiveModuleEditable}
          />
        ) : activeModule.template_type === "text" && activeModuleSchema?.type === "text" ? (
          <TextModulePanel
            schema={activeModuleSchema}
            moduleId={activeModule.id}
            activeSeriesId={activeSeriesId}
            texts={texts}
            onUpsertText={onUpsertText}
            readOnly={!isActiveModuleEditable}
          />
        ) : activeModule.template_type === "label_raster" && labelRaster ? (
          <LabelRasterModulePanel {...labelRaster} />
        ) : (
          <div className="text-[11px] text-muted-foreground">No UI for "{activeModule.template_type}"</div>
        )}
      </div>

      {activeModuleLockReason && (
        <div className="border-t border-border px-3 py-2 bg-muted/30 shrink-0">
          <div className="text-[10px] text-muted-foreground flex items-start gap-1.5">
            <Lock className="size-3 shrink-0 mt-[1px]" />
            <span>{activeModuleLockReason}</span>
          </div>
        </div>
      )}

      {completionErrors && completionErrors.length > 0 && !isActiveModuleComplete && (
        <div className="border-t border-border px-3 py-2 bg-destructive/10 shrink-0">
          <div className="text-[10px] text-destructive flex items-start gap-1.5">
            <AlertCircle className="size-3 shrink-0 mt-[1px]" />
            <div className="flex flex-col gap-0.5 min-w-0">
              <span className="font-semibold">Cannot mark complete yet:</span>
              <ul className="list-disc pl-4 space-y-0.5">
                {completionErrors.map((msg) => (
                  <li key={msg} className="break-words">
                    {msg}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="border-t border-border p-2.5 shrink-0">
        <Button
          className="w-full h-8 text-[11px]"
          variant={isActiveModuleComplete ? "outline" : "default"}
          disabled={!(canComplete && isActiveModuleEditable)}
          onClick={onMarkComplete}
        >
          <Check className="size-3.5 mr-1.5" />
          {isActiveModuleComplete ? "Mark Incomplete" : "Mark Complete"}
        </Button>
      </div>
    </aside>
  )
}
